import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import {
  EditConnectionDialogProps,
  EditConnectionDialogState,
  EditConnectionMultiRowState,
  EditConnectionRowProps,
  PasswordUnchanged,
  PerConnectionFieldState,
  StateAndProps
} from '~src/connectivity/components/EditConnection/EditConnectionAction';
import { ReadOnlyEditableConnection } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionTypes';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { OAuthEditModelLoader } from '~src/connectivity/components/EditConnection/utils/OAuthEditModelLoader';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';
import { DatabaseConnectionDetailInfo, RemoteConnectionDetailInfo } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';

import {
  CredentialOption, EditableConnection,
  ITestConnectionResult,
  SalesforceConnectionType
} from '../EditConnection/IEditConnectionDialog';
import { EditOAuthConnectionDialogBody, EditOAuthConnectionWifDialogBody } from './components/OAuth/EditOAuthConnectionDialog';
import { EditSalesforceConnectionDialogBody } from './components/Salesforce/EditSalesforceConnectionDialog';
import { EditOAuthConnectionUtils } from './utils/EditOAuthConnectionUtils';

export type EditOAuthConnectionDialogProps = Readonly<{
    firstConnection: EditableConnection;
    prompt: string;
    promptEmbedCredentials: string;
    promptNoCredential: string;
    describeEmbedCredential: string;
    describeNoCredential: string;
    addNewOAuthCredentialOptionValue: string;
    currentConnectionTypeDisplayName: string;
    showNetworkType: boolean;
    isTestConnectionDisabled: (
        dialogState: EditOAuthConnectionDialogState,
    ) => boolean;
    testConnection: (
        dialogState: EditOAuthConnectionDialogState,
    ) => Promise<ITestConnectionResult>;
}>;

export type EditOAuthConnectionDialogState = Readonly<{
    credentialOption: CredentialOption;
    credentialId: string;
    credentialNoValueOptionText: string;
    credentials: IOAuthCredential[];
    username: string;
    password: string;
    isPasswordEmbedded: boolean;
    usingManagedKeychain?: boolean;
    usingRemoteQueryAgent?: boolean;
}>;
export namespace EditOAuthConnectionAction {

    /**
     * The code below assumes that all the connections are of the same type
     * @param connections
     */
    export async function confirmEditOAuthDatabaseConnections(
        connections: EditableConnection[]
    ): Promise<Promise<any>> {

        // W-23547170: a WIF-eligible selection opens the per-connection WIF body and saves through the
        // shared per-row applyConnectionEdits, which saves each connection independently (never
        // connections[0]'s service account onto all) and surfaces a server-rejected save as a failure
        // toast. A plain OAuth / keypair / Salesforce selection keeps the existing flat single-state
        // body and flat save unchanged.
        if (EditConnectionReactActionUtils.isWifEligible(connections)) {
            return confirmEditWifEligibleConnections(connections);
        }

        const { editConnectionDialogProps, editConnectionDialogState } = await createDialogPropsAndInitialState(connections);
        const modalInstance = openReactActionDialog<EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState, IResult>({
            title: Messages.editConnection(),
            bodyComponent: connections[0].connectionType === SalesforceConnectionType ? EditSalesforceConnectionDialogBody : EditOAuthConnectionDialogBody,
            initialBodyProps: editConnectionDialogProps,
            initialDialogState: editConnectionDialogState,
            actionName: Messages.save(),
            action: (_props, state) => EditOAuthConnectionUtils.updateOAuthConnectionsToServer(
                connections,
                state.credentialOption,
                state.username,
                state.password,
                state.credentialId,
                state.credentialOption === 'password',
                state.usingRemoteQueryAgent
            ),
            dialogWidth: TabDialogWidth.width500px,
            dismissDialogOnActionFailed: true

        });
        return modalInstance.result;
    }

    // W-23547170: the WIF-eligible arm. Opens the per-connection WIF body over an
    // EditConnectionMultiRowState seed and saves through the shared per-row applyConnectionEdits, the
    // same orchestration the unified dialog uses. Its per-id updateConnections keeps each connection's
    // service account distinct (BLOCKER 1) and its .catch(rejectionToResult) + notifyAggregatedResult
    // surface a rejected save as a failure toast rather than a silent dismiss (BLOCKER 2).
    async function confirmEditWifEligibleConnections(
        connections: EditableConnection[]
    ): Promise<Promise<any>> {
        const { editConnectionDialogProps, editConnectionDialogState } = await createWifStateAndProps(connections);
        const modalInstance = openReactActionDialog<EditConnectionDialogProps, EditConnectionMultiRowState, IResult>({
            title: Messages.editConnection(),
            bodyComponent: EditOAuthConnectionWifDialogBody,
            initialBodyProps: editConnectionDialogProps,
            initialDialogState: editConnectionDialogState,
            actionName: Messages.save(),
            action: (props, dialogState) =>
                EditConnectionReactActionUtils.applyConnectionEdits(props.rows, dialogState.rows),
            dialogWidth: TabDialogWidth.width500px,
            dismissDialogOnActionFailed: true,
            // Save is disabled while any row's WIF state is invalid, or while a stored-WIF row is on
            // oauth-embedded without a credentialId (would clear to NoAuth while showing OAuth).
            // W-23514330.
            actionDisabled: (bodyProps: EditConnectionDialogProps, dialogState: EditConnectionMultiRowState) => {
                const rowPropById = new Map(bodyProps.rows.map((p) => [p.connectionId, p]));
                return dialogState.rows.some((rowState) => !EditConnectionReactActionUtils.isWifStateValid(rowState))
                    || dialogState.rows.some((rowState) => {
                        if (EditConnectionReactActionUtils.deriveAuthRadio(rowState) !== 'oauth-embedded') {
                            return false;
                        }
                        const rowProp = rowPropById.get(rowState.connectionId);
                        return !EditConnectionReactActionUtils.isWifToOauthSaveAllowed(
                            rowProp?.isStoredWif, rowState.credentialId);
                    });
            }
        });
        return modalInstance.result;
    }

    // W-23547170: build the per-connection WIF seed by reusing the shared helpers the unified dialog
    // used before its WIF surface was stripped -- wifModesForConnectionType, toWifAuthMode,
    // coerceUnifiedCredentialOption, OAuthEditModelLoader.load. It deliberately does not call the
    // unified dialog's createDialogStateAndProps (private to that namespace, and its WIF seed is gone),
    // and it does not render server/port/username inputs: the OAuth dialog never edited those for a
    // plain-OAuth connection, so the WIF path stays consistent with it (OD-3).
    async function createWifStateAndProps(connections: EditableConnection[]): Promise<StateAndProps> {
        // The OAuth credential model is loaded per row for the account picker; the common-credential
        // lookup runs against each single connection.
        const oauthModels = await Promise.all(connections.map((connection) =>
            EditConnectionReactActionUtils.isOAuthEmbeddedApplicable(connection.connectionType, connection.connectionOAuth)
                ? OAuthEditModelLoader.load([connection])
                : Promise.resolve(undefined)));

        const rowProps: EditConnectionRowProps[] = [];
        const rowState: PerConnectionFieldState[] = [];

        connections.forEach((connection, index) => {
            const wifModesAvailable = EditConnectionReactActionUtils.wifModesForConnectionType(
                connection.connectionType, connection.connectionOAuth);
            const oauthModel = oauthModels[index];
            const isOAuthEmbeddedApplicable = !!oauthModel;
            // Seed WIF-ness from the stored workgroupAuthMode, independent of the credential model:
            // under Option A a WIF connection reports authenticationType "oauth", so reading WIF-ness
            // from the credential would show it as OAuth and a plain re-save would strip WIF.
            const storedWorkgroupAuthMode = EditConnectionReactActionUtils.toWifAuthMode(
                (connection.connectionDetails as DatabaseConnectionDetailInfo).workgroupAuthMode);

            rowProps.push({
                connectionId: connection.id,
                rowLabel: EditConnectionReactActionUtils.rowLabel(connection, index),
                // The WIF body renders the chooser, not server/port/username value-rows (OD-3), so the
                // field-visibility props stay false; the stored field values are re-saved unchanged by
                // the shared save.
                isServerNameVisible: false,
                isServerPortVisible: false,
                isServerNameDisabled: false,
                isServerPortDisabled: false,
                isUsernameVisible: false,
                isEmbeddedToggleVisible: false,
                isTestConnectionButtonVisible: connection.testingConnectionAllowed,
                showNetworkType: getSessionManagementServiceInstance().isOnlineEnvironment()
                    && EditConnectionReactActionUtils.showNetworkType(
                        ((connection as IDataConnectionInfo).datasource) as IDataSourceInfo),
                isWifSupported: wifModesAvailable.length > 0,
                wifModesAvailable: wifModesAvailable,
                isOAuthEmbeddedApplicable: isOAuthEmbeddedApplicable,
                // Captured once from the stored connection so the OAuth-embedded revert-disable stays
                // fixed as the user changes the radio (reading it from mutable row state would
                // un-disable the moment the user moved off WIF).
                isStoredWif: !!storedWorkgroupAuthMode,
                usingManagedKeychain: !!(oauthModel && oauthModel.model.usingManagedKeychain),
                addNewOAuthCredentialOptionValue: oauthModel && oauthModel.addNewOAuthCredentialOptionValue,
                currentConnectionTypeDisplayName: connection.connectionTypeDisplayName,
                connection: connection,
                onTestConnection: (state: EditConnectionDialogState) =>
                    EditConnectionReactActionUtils.checkWifConnection(connection.id, state)
            });

            rowState.push({
                connectionId: connection.id,
                serverName: (connection.connectionDetails as RemoteConnectionDetailInfo).serverName,
                serverPort: (connection.connectionDetails as RemoteConnectionDetailInfo).serverPort,
                username: (connection.connectionDetails as RemoteConnectionDetailInfo).databaseUsername,
                isPasswordEmbedded: (connection.connectionDetails as RemoteConnectionDetailInfo).hasEmbeddedPassword,
                newPassword: PasswordUnchanged,
                usingRemoteQueryAgent: connection.usingRemoteQueryAgent,
                workgroupAuthMode: storedWorkgroupAuthMode,
                authFederationServiceAccount: (connection.connectionDetails as DatabaseConnectionDetailInfo).authFederationServiceAccount,
                // The chooser understands only oauth/keypair/none; coerce an unexpected password/mixed
                // model to none so it never shows the OAuth radio over an empty dropdown.
                credentialOption: oauthModel
                    ? EditConnectionReactActionUtils.coerceUnifiedCredentialOption(oauthModel.model.credentialOption)
                    : 'none',
                credentialId: oauthModel && oauthModel.model.credentialId,
                credentials: oauthModel ? oauthModel.credentials : [],
                credentialNoValueOptionText: oauthModel && oauthModel.model.credentialNoValueOptionText
            });
        });

        return {
            editConnectionDialogProps: {
                prompt: EditOAuthConnectionUtils.makePrompt(connections.length),
                rows: rowProps
            },
            editConnectionDialogState: { rows: rowState }
        };
    }

    async function createDialogPropsAndInitialState(connections: EditableConnection[]) {
        const firstConnectionType = connections[0].connectionType;
        const firstConnection: ReadOnlyEditableConnection  = connections[0];

        const datasource: IDataSourceInfo = ((firstConnection as IDataConnectionInfo).datasource) as IDataSourceInfo;

        const oauthEditModel = await OAuthEditModelLoader.load(connections);
        const oauthModel = oauthEditModel.model;
        const credentials = oauthEditModel.credentials;

        const testConnection = (
            dialogState: EditOAuthConnectionDialogState,
        ): Promise<ITestConnectionResult> => {
            const usernamePassword = { username: dialogState.username, password: { isEmbedded: dialogState.isPasswordEmbedded, newPassword: dialogState.password } };
            return dialogState.credentialOption === 'password'
                ? EditOAuthConnectionUtils.checkUsernamePasswordConnection(connections[0].id, usernamePassword)
                : EditOAuthConnectionUtils.checkOAuthConnection(dialogState.credentialId);
        };


        const isTestConnectionDisabled = (
            dialogState: EditOAuthConnectionDialogState,
        ): boolean => {
            const showTestConnection = connections.length === 1
                && OAuthCredentialsService.supportsTestConnection(firstConnectionType)
                && connections[0].testingConnectionAllowed;
            if (!showTestConnection) {
                return true;
            }
            return dialogState.credentialOption === 'mixed'
                || (dialogState.credentialOption === 'password' && !dialogState.username)
                || ((dialogState.credentialOption === 'oauth' || dialogState.credentialOption === 'keypair') && !dialogState.credentialId)
                || (dialogState.credentialOption === 'none')
                || (dialogState.usingRemoteQueryAgent === true);
        };

        const displayStrings = OAuthCredentialsService.getDisplayStrings(firstConnectionType);


        const editConnectionDialogState: EditOAuthConnectionDialogState = {
            credentialOption: oauthModel.credentialOption,
            credentialId: oauthModel.credentialId,
            credentialNoValueOptionText: oauthModel.credentialNoValueOptionText,
            credentials: credentials,
            username: oauthModel.usernamePassword.username,
            password: oauthModel.usernamePassword.password.newPassword,
            isPasswordEmbedded: oauthModel.usernamePassword.password.isEmbedded,
            usingManagedKeychain: oauthModel.usingManagedKeychain,
            usingRemoteQueryAgent: oauthModel.usingRemoteQueryAgent,
        };
        const editConnectionDialogProps: EditOAuthConnectionDialogProps = {
            firstConnection: connections[0],
            prompt: EditOAuthConnectionUtils.makePrompt(connections.length),
            promptEmbedCredentials: displayStrings.promptEmbedCredential,
            promptNoCredential: displayStrings.noCredential,
            describeEmbedCredential: displayStrings.describeEmbedCredential,
            describeNoCredential: displayStrings.describeNoCredential,
            addNewOAuthCredentialOptionValue: oauthEditModel.addNewOAuthCredentialOptionValue,
            currentConnectionTypeDisplayName: connections[0].connectionTypeDisplayName,
            showNetworkType: getSessionManagementServiceInstance().isOnlineEnvironment() &&
              EditConnectionReactActionUtils.showNetworkType(datasource),
            isTestConnectionDisabled: isTestConnectionDisabled,
            testConnection: testConnection,
        };

        return { editConnectionDialogProps, editConnectionDialogState };
    }
}